/**
 * Bipartite Graph Data Adapter
 * 
 * Adapts existing data structures (profile.json, jd.json, matching_results.json, resume_config.json)
 * into a clean bipartite graph model:
 * 
 * - Nodes: Profile items and Criteria items
 * - Edges: Connections between them (from matching_results)
 * - Selection: Which nodes are used in resume (from resume_config)
 * 
 * This is a VIEW LAYER - it doesn't change the underlying data structures.
 */

/**
 * Extract all profile item nodes from profile data
 * @param {Object} profile - Profile data from profile.json
 * @returns {Array} Array of profile item nodes with consistent structure
 */
export function extractProfileNodes(profile) {
  if (!profile) return [];
  
  const nodes = [];
  
  // Extract from experience accomplishments
  if (profile.experience) {
    profile.experience.forEach(exp => {
      if (exp.accomplishments) {
        exp.accomplishments.forEach(acc => {
          // Generate ID same format as ProfileTab: experience-{company}-{text}
          const accText = typeof acc === 'string' ? acc : (acc.text || acc.description || '');
          const itemId = acc.id || `experience-${exp.company}-${accText}`;
          
          nodes.push({
            item_id: itemId,  // Use item_id as standard field
            id: itemId,       // Keep id for backwards compatibility
            type: 'accomplishment',
            text: accText,
            company: exp.company,
            title: exp.title,
            dates: `${exp.start_date} - ${exp.end_date}`,
            tags: acc.tags || [],
            // Annotations (will be filled in later)
            _matchingInfo: null,
            _isSelected: false
          });
        });
      }
    });
  }
  
  // Extract from highlights
  if (profile.highlights) {
    profile.highlights.forEach(highlightGroup => {
      if (highlightGroup.items) {
        highlightGroup.items.forEach(item => {
          // Generate ID same format as ProfileTab: highlight-{category}-{text}
          const itemText = typeof item === 'string' ? item : (item.text || item);
          const itemId = item.id || `highlight-${highlightGroup.title}-${itemText}`;
          
          nodes.push({
            item_id: itemId,  // Use item_id as standard field
            id: itemId,       // Keep id for backwards compatibility
            type: 'highlight',
            text: itemText,
            category: highlightGroup.title,
            tags: item.tags || [],
            // Annotations
            _matchingInfo: null,
            _isSelected: false
          });
        });
      }
    });
  }
  
  // Extract from projects
  if (profile.projects) {
    profile.projects.forEach(project => {
      // Generate ID same format as ProfileTab: project-{name}
      const itemId = project.id || `project-${project.name}`;
      
      nodes.push({
        item_id: itemId,  // Use item_id as standard field
        id: itemId,       // Keep id for backwards compatibility
        type: 'project',
        text: project.description,
        name: project.name,
        description: project.description,
        technologies: project.technologies || [],
        tags: project.tags || [],
        // Annotations
        _matchingInfo: null,
        _isSelected: false
      });
    });
  }
  
  // Extract from education (if needed for resume)
  if (profile.education) {
    profile.education.forEach(edu => {
      edu.degrees?.forEach(degree => {
        const eduId = `edu-${edu.institution}-${degree.degree_type}`;
        nodes.push({
          item_id: eduId,  // Add item_id field
          id: eduId,       // Keep id for backwards compatibility
          type: 'education',
          text: `${degree.degree_type} in ${degree.degree_specialization}`,
          institution: edu.institution,
          year: degree.year,
          // Annotations
          _matchingInfo: null,
          _isSelected: false
        });
      });
    });
  }
  
  return nodes;
}

/**
 * Extract all criteria nodes from job description
 * @param {Object} jobDescription - Job description data with criteria
 * @returns {Array} Array of criteria nodes with consistent structure
 */
export function extractCriteriaNodes(jobDescription) {
  if (!jobDescription?.criteria) return [];
  
  const nodes = [];
  const criteria = jobDescription.criteria;
  
  // Extract required criteria
  if (criteria.required) {
    criteria.required.forEach(item => {
      nodes.push({
        id: item.id,
        type: 'required',
        text: item.text,
        target_evidence: item.target_evidence,
        keywords: item.keywords || [],
        // Annotations
        _matchingInfo: null,
        _coverage: { available: 0, selected: 0 }
      });
    });
  }
  
  // Extract critical criteria
  if (criteria.critical) {
    criteria.critical.forEach(item => {
      nodes.push({
        id: item.id,
        type: 'critical',
        text: item.text,
        target_evidence: item.target_evidence,
        keywords: item.keywords || [],
        // Annotations
        _matchingInfo: null,
        _coverage: { available: 0, selected: 0 }
      });
    });
  }
  
  // Extract wanted criteria
  if (criteria.wanted) {
    criteria.wanted.forEach(item => {
      nodes.push({
        id: item.id,
        type: 'wanted',
        text: item.text,
        target_evidence: item.target_evidence,
        keywords: item.keywords || [],
        // Annotations
        _matchingInfo: null,
        _coverage: { available: 0, selected: 0 }
      });
    });
  }
  
  // Extract nice-to-have criteria
  if (criteria.nice_to_have) {
    criteria.nice_to_have.forEach(item => {
      nodes.push({
        id: item.id,
        type: 'nice_to_have',
        text: item.text,
        target_evidence: item.target_evidence,
        keywords: item.keywords || [],
        // Annotations
        _matchingInfo: null,
        _coverage: { available: 0, selected: 0 }
      });
    });
  }
  
  return nodes;
}

/**
 * Extract edges from matching results
 * Matching results has TWO views - we'll support both
 */
export class MatchingGraph {
  constructor(matchingResults) {
    this.matchingResults = matchingResults;
    this.matchingOutput = matchingResults?.matching_output || null;
  }
  
  /**
   * Find edges FROM a profile item (what criteria does it address?)
   * @param {string} profileItemId 
   * @returns {Array} Edges with criterion_id, strength, clarity, reasoning
   */
  findEdgesFrom(profileItemId) {
    if (!this.matchingOutput?.profile_items) return [];
    
    const item = this.matchingOutput.profile_items.find(i => i.id === profileItemId);
    if (!item?.addresses_criteria) return [];
    
    return item.addresses_criteria.map(edge => ({
      from: profileItemId,
      to: edge.criterion_id,
      strength: edge.strength,
      clarity: edge.clarity,
      reasoning: edge.reasoning,
      keywords: edge.supporting_keywords || []
    }));
  }
  
  /**
   * Find edges TO a criterion (what profile items address it?)
   * @param {string} criterionId 
   * @returns {Array} Edges with profile_item_id, strength, clarity, snippet
   */
  findEdgesTo(criterionId) {
    if (!this.matchingOutput?.job_criteria) return [];
    
    // Search across all criterion types
    const allCriteria = [
      ...(this.matchingOutput.job_criteria.required || []),
      ...(this.matchingOutput.job_criteria.critical || []),
      ...(this.matchingOutput.job_criteria.wanted || []),
      ...(this.matchingOutput.job_criteria.nice_to_have || [])
    ];
    
    const criterion = allCriteria.find(c => c.id === criterionId);
    // Check for both available_evidence and current_evidence for backwards compatibility
    const evidence = criterion?.available_evidence || criterion?.current_evidence || [];
    if (evidence.length === 0) return [];
    
    return evidence.map(edge => ({
      from: edge.item_id,
      to: criterionId,
      strength: edge.strength,
      clarity: edge.clarity,
      snippet: edge.snippet
    }));
  }
  
  /**
   * Get all edges (for graph visualization)
   * @returns {Array} All edges in the graph
   */
  getAllEdges() {
    if (!this.matchingOutput?.profile_items) return [];
    
    const edges = [];
    this.matchingOutput.profile_items.forEach(item => {
      if (item.addresses_criteria) {
        item.addresses_criteria.forEach(match => {
          edges.push({
            from: item.id,
            to: match.criterion_id,
            strength: match.strength,
            clarity: match.clarity,
            reasoning: match.reasoning
          });
        });
      }
    });
    
    return edges;
  }
}

/**
 * Annotate profile nodes with matching information
 * @param {Array} profileNodes - Profile item nodes
 * @param {MatchingGraph} matchingGraph - Matching graph
 */
export function annotateProfileWithMatching(profileNodes, matchingGraph) {
  if (!matchingGraph) return;
  
  profileNodes.forEach(node => {
    const edges = matchingGraph.findEdgesFrom(node.id);
    
    node._matchingInfo = {
      addressesCriteria: edges.map(e => e.to),
      strengths: edges.map(e => e.strength),
      clarities: edges.map(e => e.clarity),
      count: edges.length,
      edges: edges
    };
  });
}

/**
 * Annotate criteria nodes with matching information
 * @param {Array} criteriaNodes - Criteria item nodes
 * @param {MatchingGraph} matchingGraph - Matching graph
 */
export function annotateCriteriaWithMatching(criteriaNodes, matchingGraph) {
  if (!matchingGraph) return;
  
  criteriaNodes.forEach(node => {
    const edges = matchingGraph.findEdgesTo(node.id);
    
    node._matchingInfo = {
      addressedBy: edges.map(e => e.from),
      strengths: edges.map(e => e.strength),
      clarities: edges.map(e => e.clarity),
      count: edges.length,
      edges: edges
    };
  });
}

/**
 * Annotate nodes with selection state from resume config
 * @param {Array} profileNodes - Profile item nodes
 * @param {Object} resumeConfig - Resume config with selected_items
 */
export function annotateWithSelection(profileNodes, resumeConfig) {
  if (!resumeConfig?.selected_items) return;
  
  const selectedIds = new Set(resumeConfig.selected_items);
  
  profileNodes.forEach(node => {
    node._isSelected = selectedIds.has(node.id);
  });
}

/**
 * Update criteria coverage based on selection
 * @param {Array} criteriaNodes - Criteria nodes
 * @param {Array} profileNodes - Profile nodes (with selection)
 */
export function updateCriteriaCoverage(criteriaNodes, profileNodes) {
  criteriaNodes.forEach(criterion => {
    if (!criterion._matchingInfo) return;
    
    const available = criterion._matchingInfo.count;
    const selected = criterion._matchingInfo.addressedBy.filter(itemId => {
      const node = profileNodes.find(n => n.id === itemId);
      return node?._isSelected;
    }).length;
    
    criterion._coverage = { available, selected };
  });
}

/**
 * Main function: Load and integrate all data
 * @param {Object} profile - Profile data
 * @param {Object} jobDescription - Job description with criteria
 * @param {Object} matchingResults - Matching results
 * @param {Object} resumeConfig - Resume configuration
 * @returns {Object} Integrated graph data
 */
export function integrateGraphData(profile, jobDescription, matchingResults, resumeConfig) {
  // Extract nodes
  const profileNodes = extractProfileNodes(profile);
  const criteriaNodes = extractCriteriaNodes(jobDescription);
  
  // Create matching graph
  const matchingGraph = matchingResults ? new MatchingGraph(matchingResults) : null;
  
  // Annotate nodes with matching info
  if (matchingGraph) {
    annotateProfileWithMatching(profileNodes, matchingGraph);
    annotateCriteriaWithMatching(criteriaNodes, matchingGraph);
  }
  
  // Annotate with selection
  if (resumeConfig) {
    annotateWithSelection(profileNodes, resumeConfig);
  }
  
  // Update coverage
  if (matchingGraph && resumeConfig) {
    updateCriteriaCoverage(criteriaNodes, profileNodes);
  }
  
  return {
    profileNodes,
    criteriaNodes,
    matchingGraph,
    // Helper methods
    getSelectedNodes: () => profileNodes.filter(n => n._isSelected),
    getNodeById: (itemId) => profileNodes.find(n => n.item_id === itemId) || criteriaNodes.find(n => n.id === itemId),
    toggleSelection: (itemId) => {
      const node = profileNodes.find(n => n.item_id === itemId);

      if (node) {
        node._isSelected = !node._isSelected;
        updateCriteriaCoverage(criteriaNodes, profileNodes);
        return node._isSelected;
      }
      return false;
    }
  };
}

export default {
  extractProfileNodes,
  extractCriteriaNodes,
  MatchingGraph,
  annotateProfileWithMatching,
  annotateCriteriaWithMatching,
  annotateWithSelection,
  updateCriteriaCoverage,
  integrateGraphData
};
